import { useEffect, useMemo, useState } from 'react';
import { SlidersHorizontal, X } from '@phosphor-icons/react';
import { useCatalogs } from '@/hooks/useTasks';
import {
  useCapacidadDefaults,
  useEffectiveCapacidad,
  useUpsertCapacidadDefault,
  useUpsertCapacidadOverride,
  useDeleteCapacidadOverride,
} from '@/hooks/useCapacidades';
import type { EffectiveCapacidad } from '@/types';

// Modal de capacidades del modo planning, scopeado al sprint objetivo.
// Por usuario se edita:
//   - Horas disponibles  -> override del sprint (vacío = borra el override y
//     vuelve al default). Decisión del usuario: disponibles por sprint.
//   - Revisión           -> horas de revisión del DEFAULT del usuario.
// Y se muestra la barra Asignadas / Disponibles (sube en vivo al asignar).
export function PlanningCapacidadesModal({
  sprintId, sprintNombre, onClose,
}: {
  sprintId: number;
  sprintNombre: string;
  onClose: () => void;
}) {
  const { data: catalogs } = useCatalogs();
  const { data: defaults = [] } = useCapacidadDefaults();
  const { data: effective = [] } = useEffectiveCapacidad(sprintId);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const defByCol = useMemo(() => {
    const m = new Map<number, typeof defaults[number]>();
    defaults.forEach((d) => m.set(d.idCol, d));
    return m;
  }, [defaults]);
  const effByCol = useMemo(() => {
    const m = new Map<number, EffectiveCapacidad>();
    effective.forEach((e) => m.set(e.idCol, e));
    return m;
  }, [effective]);

  const users = catalogs?.users ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 sm:pt-12" onClick={onClose}>
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-stone-200 px-5 py-3">
          <h2 className="flex items-center gap-2 text-base font-semibold text-stone-900">
            <SlidersHorizontal size={18} weight="bold" /> Capacidades del sprint
          </h2>
          <button onClick={onClose} className="rounded p-1 hover:bg-stone-100" aria-label="Cerrar">
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="border-b border-stone-100 px-5 py-2 text-xs text-stone-500">
          {sprintNombre} · <strong>Disponibles</strong> = override de este sprint · <strong>Revisión</strong> = default del usuario
        </div>

        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 border-b border-stone-200 bg-stone-50 text-xs uppercase text-stone-500">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Colaborador</th>
                <th className="w-28 px-4 py-2 text-right font-medium">Disponibles</th>
                <th className="w-24 px-4 py-2 text-right font-medium">Revisión</th>
                <th className="w-44 px-4 py-2 text-right font-medium">Asignadas / Disp.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {users.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center italic text-stone-500">Cargando…</td></tr>
              )}
              {users.map((u) => (
                <Row
                  key={u.idCol}
                  idCol={u.idCol}
                  sprintId={sprintId}
                  nombre={u.nombreCompleto || u.nombre || `#${u.idCol}`}
                  defaultHoras={defByCol.get(u.idCol)?.horasDefault ?? null}
                  revisionHoras={defByCol.get(u.idCol)?.horasRevision ?? null}
                  effective={effByCol.get(u.idCol) ?? null}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Row({
  idCol, sprintId, nombre, defaultHoras, revisionHoras, effective,
}: {
  idCol: number;
  sprintId: number;
  nombre: string;
  defaultHoras: number | null;
  revisionHoras: number | null;
  effective: EffectiveCapacidad | null;
}) {
  const upsertDefault = useUpsertCapacidadDefault();
  const upsertOverride = useUpsertCapacidadOverride();
  const deleteOverride = useDeleteCapacidadOverride();

  const [ovValue, setOvValue] = useState(effective?.tieneOverride ? String(effective.horasEfectivas) : '');
  const [revValue, setRevValue] = useState(revisionHoras !== null ? String(revisionHoras) : '');

  useEffect(() => {
    setOvValue(effective?.tieneOverride ? String(effective.horasEfectivas) : '');
  }, [effective?.tieneOverride, effective?.horasEfectivas]);
  useEffect(() => {
    setRevValue(revisionHoras !== null ? String(revisionHoras) : '');
  }, [revisionHoras]);

  function parseHoras(s: string, fallback: number): number {
    const n = parseFloat(s.trim().replace(',', '.'));
    return isNaN(n) || n < 0 || n > 168 ? fallback : n;
  }

  // Disponibles -> override del sprint. Vacío + tenía override = borrar.
  function commitOverride() {
    const trimmed = ovValue.trim().replace(',', '.');
    if (trimmed === '') {
      if (effective?.tieneOverride) deleteOverride.mutate({ idSprint: sprintId, idCol });
      return;
    }
    const n = parseFloat(trimmed);
    if (isNaN(n) || n < 0 || n > 168) {
      setOvValue(effective?.tieneOverride ? String(effective.horasEfectivas) : '');
      return;
    }
    if (effective?.tieneOverride && n === effective.horasEfectivas) return;
    upsertOverride.mutate({ idSprint: sprintId, idCol, horasOverride: n });
  }

  // Revisión -> default del usuario (el upsert escribe ambos campos).
  function commitRevision() {
    const trimmed = revValue.trim().replace(',', '.');
    if (trimmed === '') { setRevValue(revisionHoras !== null ? String(revisionHoras) : ''); return; }
    const n = parseFloat(trimmed);
    if (isNaN(n) || n < 0 || n > 168) { setRevValue(revisionHoras !== null ? String(revisionHoras) : ''); return; }
    if (revisionHoras !== null && n === revisionHoras) return;
    upsertDefault.mutate({ idCol, horasDefault: parseHoras(String(defaultHoras ?? 47.5), 47.5), horasRevision: n });
  }

  const asignadas = effective?.horasAsignadas ?? 0;
  const disponibles = effective?.horasDisponibles ?? 0;
  const ratio = disponibles > 0 ? asignadas / disponibles : (asignadas > 0 ? 1 : 0);
  const fill = ratio >= 1 ? '#BB4A4A' : ratio >= 0.8 ? '#F0C802' : '#ADD010';

  return (
    <tr className="hover:bg-stone-50">
      <td className="px-4 py-2 text-stone-800">{nombre}</td>
      <td className="px-4 py-2 text-right">
        <input
          type="number" min={0} max={168} step={0.5}
          value={ovValue}
          onChange={(e) => setOvValue(e.target.value)}
          onBlur={commitOverride}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          placeholder={defaultHoras !== null ? String(defaultHoras) : '—'}
          title={effective?.tieneOverride ? 'Vacío = borrar override (vuelve al default)' : 'Override solo de este sprint'}
          className={`w-20 rounded border-0 px-2 py-1 text-right text-sm focus:bg-white focus:ring-2 focus:ring-brand-green-100 ${
            effective?.tieneOverride ? 'bg-amber-50' : 'bg-stone-100'
          }`}
        />
      </td>
      <td className="px-4 py-2 text-right">
        <input
          type="number" min={0} max={168} step={0.5}
          value={revValue}
          onChange={(e) => setRevValue(e.target.value)}
          onBlur={commitRevision}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          placeholder="—"
          title="Horas de revisión (default del usuario)"
          className="w-16 rounded border-0 bg-stone-100 px-2 py-1 text-right text-sm focus:bg-white focus:ring-2 focus:ring-brand-green-100"
        />
      </td>
      <td className="px-4 py-2">
        <div className="flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-stone-200">
            <div className="h-full" style={{ width: `${Math.min(ratio, 1) * 100}%`, background: fill }} />
          </div>
          <span className="whitespace-nowrap font-mono text-[11px] text-stone-600">
            {asignadas.toFixed(1)}/{disponibles.toFixed(0)}h
          </span>
        </div>
      </td>
    </tr>
  );
}
